/* eslint-disable relay/unused-fields */
import { User } from '@auth0/auth0-react'
import { Alert } from '@dittolive/anvil'
import * as Sentry from '@sentry/browser'
import graphql from 'babel-plugin-relay/macro'
import { useCreateHubSpotContact, useTrackContactVisit } from 'components/hooks'
import { FullPageLoader } from 'components/loader'
import React, { useEffect, useRef, useState } from 'react'
import { useLazyLoadQuery, useRefetchableFragment } from 'react-relay/hooks'
import { usePortalMutation } from 'utils'
import { useAnalytics } from 'utils/analytics'
import { captureMessageWithContext } from 'utils/sentry/tracking'

import { UserSync_viewer$key } from './__generated__/UserSync_viewer.graphql'
import { UserSyncEmailMutation } from './__generated__/UserSyncEmailMutation.graphql'
import { UserSyncLoginUserMutation } from './__generated__/UserSyncLoginUserMutation.graphql'
import { UserSyncMutation } from './__generated__/UserSyncMutation.graphql'
import { UserSyncQuery } from './__generated__/UserSyncQuery.graphql'
import { UserSyncQueryRefetch } from './__generated__/UserSyncQueryRefetch.graphql'
import { useInitLD } from './useInitLD'

const CurrentUserFragment = graphql`
  fragment UserSync_viewer on Viewer
  @refetchable(queryName: "UserSyncQueryRefetch") {
    me {
      id
      email
      isSuperAdmin
    }
  }
`

const query = graphql`
  query UserSyncQuery {
    viewer {
      ...UserSync_viewer
    }
  }
`

export const CreateUnknownUserMutation = graphql`
  mutation UserSyncMutation($input: CreateUserInput!) {
    createUser(input: $input) {
      __typename
    }
  }
`

export const LoginUserMutation = graphql`
  mutation UserSyncLoginUserMutation {
    loginUser {
      __typename
    }
  }
`

export const SyncEmailMutation = graphql`
  mutation UserSyncEmailMutation {
    syncUserEmail {
      __typename
    }
  }
`

type Props = {
  /** User data extracted from auth0. */
  user: User
  /** Children. */
  children: (
    userInfo: {
      readonly id: string
      readonly email: string
      readonly isSuperAdmin: boolean
    } | null,
  ) => React.ReactNode
}

/** Component used to sync the current user data to the portal
 * when a user with a valid JWT token doesn't exist on the server.
 *
 * This ensures that all users authenticated through the Auth provider
 * have been saved on our server.
 */
export default function UserSync({ user, children }: Props) {
  const { setUserId, logEvent } = useAnalytics()
  const [didRefetchViewer, setDidRefetchViewer] = useState(false)
  const { viewer } = useLazyLoadQuery<UserSyncQuery>(query, {})
  const [data, refetch] = useRefetchableFragment<
    UserSyncQueryRefetch,
    UserSync_viewer$key
  >(CurrentUserFragment, viewer)
  const [createUser] = usePortalMutation<UserSyncMutation>(
    CreateUnknownUserMutation,
    {
      mutationName: 'CreateUnknownUser',
      // A failed create is surfaced through the viewer refetch below: either
      // the user exists by then (concurrent create won the race) and the
      // session proceeds, or the error alert renders. No error toast.
      showErrorToast: false,
      sentryContext: {
        component: 'UserSync',
      },
    },
  )
  const [syncUserEmail] = usePortalMutation<UserSyncEmailMutation>(
    SyncEmailMutation,
    {
      mutationName: 'SyncUserEmail',
      sentryContext: {
        component: 'UserSync',
      },
    },
  )
  const [loginUser] = usePortalMutation<UserSyncLoginUserMutation>(
    LoginUserMutation,
    {
      mutationName: 'LoginUser',
      sentryContext: {
        component: 'UserSync',
      },
    },
  )
  const viewerId = data?.me?.id
  const currentEmail = data?.me?.email
  const currentName = user.name
  const hubspotContactId = useCreateHubSpotContact(viewerId)
  useTrackContactVisit(hubspotContactId)
  useInitLD({
    key: viewerId,
    email: currentEmail,
    name: currentName,
    isRunningCypress: window.Cypress !== undefined,
  })

  useEffect(() => {
    if (viewerId && currentEmail) {
      setUserId(viewerId, currentEmail)

      Sentry.setUser({ id: viewerId, email: currentEmail })
    }
  }, [viewerId, currentEmail, setUserId])

  const createUserInFlight = useRef(false)

  useEffect(() => {
    const refetchViewer = () => {
      refetch(
        {},
        {
          fetchPolicy: 'network-only',
          onComplete: () => {
            setDidRefetchViewer(true)
          },
        },
      )
    }

    if (!viewerId && user && !didRefetchViewer && !createUserInFlight.current) {
      createUserInFlight.current = true
      createUser({
        variables: {
          input: {
            auth0Id: user.sub!,
            firstName: user.given_name,
            lastName: user.family_name,
            email: user.email!,
            imageUrl: user.picture,
          },
        },
        onCompleted() {
          createUserInFlight.current = false
          logEvent('createdAccount')
          refetchViewer()
        },
        onError() {
          createUserInFlight.current = false
          refetchViewer()
        },
      })
    } else if (viewerId) {
      logEvent('userLogin')
    }
  }, [viewerId, didRefetchViewer, createUser, user, refetch, logEvent])

  useEffect(() => {
    if (viewerId) {
      loginUser({ variables: {} })
    }
  }, [viewerId, loginUser])

  /** An effect to update portal emails if they don't match the one in Auth0. */
  useEffect(() => {
    if (viewerId && user.email && currentEmail !== user.email) {
      // Only sync emails for Ditto accounts using Google SSO, report to Sentry otherwise.
      if (
        !user.sub?.startsWith('google-oauth2|') ||
        !/@ditto\.(live|com)$/i.test(currentEmail ?? '')
      ) {
        captureMessageWithContext(
          'auth.email_mismatch',
          { component: 'UserSync' },
          'error',
        )
        return
      }

      syncUserEmail({
        variables: {},
        onError: (err) => {
          Sentry.withScope((scope) => {
            scope.setTag('UserSync', 'error trying to sync email')
            scope.setExtras({
              currentEmail,
              newEmail: user.email,
              auth0Id: user.sub,
            })
            Sentry.captureException(err)
          })
        },
      })
    }
  }, [viewerId, currentEmail, user.sub, user.email, syncUserEmail])

  if (!viewerId) {
    return didRefetchViewer ? (
      <div className="py-12">
        <Alert variant="danger">
          There was an error starting the portal session.
        </Alert>
      </div>
    ) : (
      <FullPageLoader />
    )
  }

  // Can't return children directly
  // https://github.com/DefinitelyTyped/DefinitelyTyped/issues/18051#issuecomment-449628575
  return <>{children(data?.me)}</>
}
